import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { authAPI, profileAPI } from '../api/endpoints';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the token check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setIsLoading(false);
      return;
    }
    
    // Check if user is logged in (via httpOnly cookie)
    // No need to check localStorage - the cookie is automatically sent
    authAPI.getCurrentUser()
      .then((userData) => {
        setUser(userData);
        setIsLoading(false);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to get current user:', error);
        }
        setIsLoading(false);
      });
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const data = await authAPI.login(email, password);
      setUser(data.user);
      // No need to store token in localStorage - it's in httpOnly cookie
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      setIsLoading(false);
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const signup = async (name, username, age, location, email, password, dateOfBirth = null) => {
    setIsLoading(true);
    try {
      const data = await authAPI.signup({
        name,
        username,
        age: parseInt(age),
        location,
        email,
        password,
        photos: [],
        bio: 'Just exploring Swyper!',
        date_of_birth: dateOfBirth
      });
      setUser(data.user);
      // No need to store token in localStorage - it's in httpOnly cookie
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      setIsLoading(false);
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await authAPI.logout();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    } finally {
      setUser(null);
      // Navigate to home page
      window.location.href = '/';
    }
  };

  const updateProfile = async (updates) => {
    try {
      const updatedUser = await profileAPI.updateProfile(updates);
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Update failed');
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    setUser,
    isLoading,
    login,
    signup,
    logout,
    updateProfile
  }), [user, isLoading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
